import Spinner from 'components/Spinner/Spinner';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import { dateFormatType, INotification } from 'interfaces';
import { AgreementNotificationsEnum, ChatNotificationsEnum, DocumentNotificationEnum, InviteNotificationsEnum, loadingNotificationEnum, NotificationType, RoomNotificationsEnum, SigningNotificationsEnum } from 'interfaces/INotification';
import React from 'react';
import classes from './Notifications.module.scss';
import cn from 'classnames';
import { Avatar, Button, Tag, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hook';

type ActionsType = {
  onAccept: (event: any) => void;
  onReject: (event: any) => void;
};

const inviteTypes = Object.values(InviteNotificationsEnum);
const chatTypes = Object.values(ChatNotificationsEnum);
const roomTypes = Object.values(RoomNotificationsEnum);
const filesTypes = [
  ...Object.values(AgreementNotificationsEnum),
  ...Object.values(SigningNotificationsEnum),
  ...Object.values(DocumentNotificationEnum)
];

const getNotificationType = {
  invite: inviteTypes,
  chat: chatTypes,
  room: roomTypes,
  file: filesTypes,
}

type NotificationRowProps = {
  items: INotification[];
  index: number;
  style: any;
  onItemClick: (file_id?: string) => void;
  dateFormat: dateFormatType;
  arrUpdatingIds?: string[];
  actions: ActionsType;
};

const NotificationRow = ({
  items,
  index,
  style,
  onItemClick,
  dateFormat,
  arrUpdatingIds,
  actions,
}: NotificationRowProps) => {
  const [isActionLoading, setIsActionLoading] = React.useState(false);
  const { t } = useTranslation();

  const { messageApi } = useAppSelector((state) => state.windowState);
  
  const date = getDateWithTimeZone(items[index].created_at, dateFormat, true);
  const isHoverableItem = items[index].link || items[index].status !== 1;
  let currentTypeGroup: string = '';

  for (let key in getNotificationType) {
    const currentTypes = getNotificationType[key as keyof typeof getNotificationType] as NotificationType[];
    if (currentTypes.includes(items[index].type)) {
      currentTypeGroup = key;
    }
  };

  const getFileId = () => {
    const fileId = items[index].details?.entity_id;
    if (!fileId) {
      messageApi?.error(t('Header.Notification.message.fileNotFound'));
      return;
    }
    return fileId;
  };

  Object.keys(actions).forEach((key) => {
    const oldAction = actions[key as keyof typeof actions];
    actions[key as keyof typeof actions] = async (event: React.MouseEvent) => {
      event.stopPropagation();
      setIsActionLoading(true);
      await oldAction(event);
      setIsActionLoading(false);
    };
  });

  if (items[index].type === loadingNotificationEnum.LOADING) {
    return (<div style={style} className={classes.listItem}>
      <Spinner size={48} />
    </div>)
  };

  const getNavigateButton = (type: string, link?: string, details?: any) => {
    switch(type) {
      case 'room':
        return (<div className={classes.buttons}>
          <Button
            className={classes.linkButton}
            type='link'>
            {t('Header.Notification.roomButtons.init')} 
          </Button>
        </div>);
      case 'chat':
        return (<div className={classes.buttons}>
          <Button
            className={classes.linkButton}
            type='link'>
            {t('Header.Notification.roomButtons.toChat')} 
          </Button>
        </div>);
      case 'file':
        const fileName = details?.entity_name || t('Header.Notification.file');
        const fileType = fileName?.split('.').reverse()[0].toLowerCase();
        const icon = fileType && availableIconFormat.includes(fileType) ? fileType : 'default';
                
        return (<div className={classes.buttons} style={{padding: 0}}>
          <div 
            className={cn(
              classes.fileWrap, 
              link && classes.fileClickable
            )}
            onClick={(e) => {
              e.stopPropagation(); 
              onItemClick(link ? getFileId() : undefined)
            }}
          >
            <Avatar
              size={28}
              className={classes.avatar}
              icon={documnentIcons[icon as keyof typeof documnentIcons]}
            />
            {fileName}
          </div>
        </div>);
    };
  };

  const inviteButtonsAndTags = (type: string, status?: number) => {
    if (type === 'invite_reminder') {
      return <div className={classes.buttons}>
        <Tag color='processing'>{t('Header.Notification.inviteTags.reminder')}</Tag>
      </div>;
    } else if (type === 'invite') {
      switch(status) {
        case 0:
          return <div className={classes.buttons}>
            <Button loading={isActionLoading} onClick={actions.onReject} type='default'>
              {t('Confirm.reject')}
            </Button>
            <Button loading={isActionLoading} onClick={actions.onAccept} type='primary'>
              {t('Confirm.accept')}
            </Button>
          </div>;
        case 1:
          return <div className={classes.buttons}>
            <Tag color='success'>{t('Header.Notification.inviteTags.accepted')}</Tag>
          </div>;
        case 2:
          return <div className={classes.buttons}>
            <Tag color='error'>{t('Header.Notification.inviteTags.rejected')}</Tag>
          </div>;
        case 3:
          return <div className={classes.buttons}>
            <Tag color='warning'>{t('Header.Notification.inviteTags.expired')}</Tag>
          </div>;
        default: 
          return <div className={classes.buttons}>
            <Tag color='default'>{t('Header.Notification.inviteTags.notDefined')}</Tag>
          </div>;
      };
    };
  };
  
  return (
    <div
      style={style}
      className={cn(
        classes.listItem,
        isHoverableItem ? classes.hoverable : 'null'
      )}
      id={`notification_card_${index}`}
      onClick={() => isActionLoading && items[index].details.invite_status === 0 ? undefined : onItemClick()}
    >
      <div className={classes.infoRow}>
        <div className={classes.dotStatus}>{arrUpdatingIds!.includes(items[index].id) ? (
          <LoadingOutlined style={{ color: 'lightblue' }} />
        ) : (
          !items[index].status 
            ? <FontAwesomeIcon className={classes.dot} icon={faCircle} />
            : <FontAwesomeIcon className={classes.dotViewed} icon={faCircle} />
        )}</div>

        <div className={classes.text}>
          <Tooltip mouseEnterDelay={0.6} title={items[index].message}>
            <div className={classes.description}>{items[index].message}</div>
          </Tooltip>

          <div className={classes.timestampRow}>
            <span>{date} · {t(`Header.Notification.groupTypes.${currentTypeGroup}`)}</span>
          </div>

          {items[index].type?.includes('invite') && (
            inviteButtonsAndTags(items[index].type, items[index]?.details?.invite_status)
          )}

          { getNavigateButton(currentTypeGroup, items[index]?.link, items[index]?.details) }
        </div>
      </div>
    </div>
  );
}

export default NotificationRow;
